import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card, CardBody, Col, Container, Form, Input, Label, Row, Button, Table, FormGroup,
} from "reactstrap";
import {
  getNextPaymentNo,
  getSupplierOutstandingInvoices,
  savePayment,
} from "../../components/Api/GmsPayment";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getBankAccounts } from "../../components/Api/GmsBankAccount";
import { getBanks } from "../../components/Api/GmsBank";
import { searchSuppliers } from "../../components/Api/GmsSupplier";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  isUserAdmin,
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI } from "../../helpers/dateHelper";

const PAGE_ROUTE = "/GmsAddPayment";

const selectStyles = {
  control: (p, s) => ({ ...p, minHeight: "38px", borderColor: s.isFocused ? "#556ee6" : "#ced4da", boxShadow: s.isFocused ? "0 0 0 0.2rem rgba(85,110,230,.25)" : "none" }),
  menu: (p) => ({ ...p, zIndex: 9999, maxHeight: 200 }),
  menuList: (p) => ({ ...p, maxHeight: 180 }),
  option: (p, s) => ({ ...p, backgroundColor: s.isSelected ? "#556ee6" : s.isFocused ? "#f8f9fa" : "white", color: s.isSelected ? "white" : "#495057" }),
};

const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

const defaultForm = {
  PaymentId: 0,
  PaymentNo: "",
  PaymentDate: new Date(),
  BusinessId: null,
  BranchId: null,
  PayeeType: "sup",
  PayeeId: null,
  Amount: "",
  TaxAmount: 0,
  DiscountAmount: 0,
  NetAmount: "",
  CashAmount: "",
  CashlessAmount: 0,
  BankId: null,
  BankAccountId: null,
  PaymentReferenceNo: "",
  Notes: "",
};

export default function AddPaymentPage() {
  const [form, setForm] = useState(defaultForm);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [banks, setBanks] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [paymentAllocation, setPaymentAllocation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [totalAdvance, setTotalAdvance] = useState(0);
  const [resetCounter, setResetCounter] = useState(0);

  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const hasPermission = hasPagePermissionWithAdmin(permissions, PAGE_ROUTE);
  const isViewOnly = hasViewOnlyPermission(permissions, PAGE_ROUTE);
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, PAGE_ROUTE);

  const session = useMemo(() => getUserSession(), []);
  const isBusinessReadonly = !!session.companyId;
  const isBranchReadonly = !!session.branchId;

  useEffect(() => {
    if (!hasPermission) { window.location.href = "/Dashboard"; return; }
    loadInitialData();
    if (session.companyId) setForm(p => ({ ...p, BusinessId: session.companyId }));
    if (session.branchId) setForm(p => ({ ...p, BranchId: session.branchId }));
  }, [hasPermission]);

  useEffect(() => { if (form.BusinessId) { loadNextPaymentNo(); loadBankAccounts(); } }, [form.BusinessId]);

  useEffect(() => {
    if (form.PayeeId && form.BusinessId) loadOutstandingInvoices();
    else { setOutstandingInvoices([]); setPaymentAllocation([]); }
  }, [form.PayeeId, form.BusinessId, form.BranchId]);

  useEffect(() => {
    const net = parseFloat(form.NetAmount) || 0;
    if (net > 0 && outstandingInvoices.length > 0) allocatePayment(net);
    else setPaymentAllocation([]);
  }, [form.NetAmount, outstandingInvoices]);

  const loadInitialData = async () => {
    try {
      const [bizData, branchData, banksData] = await Promise.all([
        getBusinesses({ business_Active: true }),
        getBranches({ branch_Active: true }),
        getBanks({ bank_Active: true }),
      ]);
      setBusinesses(bizData || []);
      setBranches(branchData || []);
      setBanks(Array.isArray(banksData) ? banksData : (banksData?.banks || banksData?.items || []));
    } catch (e) { console.error(e); }
  };

  const loadBankAccounts = async () => {
    try {
      const data = await getBankAccounts({ bankAccount_Business_Id: form.BusinessId, bankAccount_Active: true });
      setBankAccounts(Array.isArray(data) ? data : (data?.bankAccounts || data?.items || []));
    } catch (e) { console.error("Bank accounts:", e); }
  };

  const loadNextPaymentNo = async () => {
    try {
      const res = await getNextPaymentNo(form.BusinessId);
      if (res.isSuccess) setForm(p => ({ ...p, PaymentNo: res.nextPaymentNo }));
    } catch (e) { console.error(e); }
  };

  const loadOutstandingInvoices = async () => {
    try {
      setLoading(true);
      const data = await getSupplierOutstandingInvoices(form.PayeeId, form.BusinessId, form.BranchId);
      
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        setOutstandingInvoices(data.invoices || []);
        setTotalAdvance(data.totalAdvance || 0);
      } else {
        setOutstandingInvoices(Array.isArray(data) ? data : []);
        setTotalAdvance(0);
      }
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to load outstanding invoices", "error");
    } finally { setLoading(false); }
  };

  // FIFO allocation
  const allocatePayment = (amount) => {
    let remaining = amount;
    const allocation = [];
    const sorted = [...outstandingInvoices].sort((a, b) => new Date(a.purchaseOrder_Date) - new Date(b.purchaseOrder_Date));
    for (const inv of sorted) {
      if (remaining <= 0) break;
      const outstanding = inv.outstanding_Amount || 0;
      const paid = Math.min(remaining, outstanding);
      const paidRounded = Math.round(paid * 100) / 100;
      allocation.push({
        PurchaseOrderId: inv.purchaseOrder_Id,
        Invoice_No: inv.purchaseOrder_InvoiceNo,
        Invoice_Date: inv.purchaseOrder_Date,
        Invoice_Amount: inv.invoice_Amount,
        OutstandingBefore: outstanding,
        PaidAmount: paidRounded,
        OutstandingAfter: Math.round((outstanding - paidRounded) * 100) / 100,
        TotalAmount: outstanding,
        SettledAmount: Math.round(((inv.paid_Amount || 0) + paidRounded) * 100) / 100,
      });
      remaining -= paidRounded;
    }
    setPaymentAllocation(allocation);
  };

  const handleFormChange = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleAmountChange = (val) => {
    const num = parseFloat(val) || 0;
    const tax = parseFloat(form.TaxAmount) || 0;
    const disc = parseFloat(form.DiscountAmount) || 0;
    const net = Math.max(0, num - disc + tax);
    setForm(p => ({ ...p, Amount: val, NetAmount: net.toFixed(2), CashAmount: net.toFixed(2), CashlessAmount: 0 }));
  };

  const handleCashAmountChange = (val) => {
    const cash = parseFloat(val) || 0;
    const net = parseFloat(form.NetAmount) || 0;
    const cashless = Math.max(0, net - cash);
    setForm(p => ({ ...p, CashAmount: val, CashlessAmount: cashless.toFixed(2) }));
  };

  const handleSave = async () => {
    if (saveLoading) return;
    setSaveLoading(true);
    try {
      if (!form.BusinessId) { Swal.fire("Error", "Please select a company", "error"); return; }
      if (!form.PayeeId) { Swal.fire("Error", "Please select a supplier", "error"); return; }
      const net = parseFloat(form.NetAmount) || 0;
      if (net <= 0) { Swal.fire("Error", "Please enter a valid amount", "error"); return; }
      if (!form.PaymentNo) { Swal.fire("Error", "Payment number is required", "error"); return; }
      if (net > totalOutstanding) { Swal.fire("Error", `Payment cannot exceed the total outstanding amount of ${fmt(totalOutstanding)}`, "error"); return; }

      const cashAmt = parseFloat(form.CashAmount) || 0;
      const cashlessAmt = parseFloat(form.CashlessAmount) || 0;

      if (Math.abs((cashAmt + cashlessAmt) - net) > 0.01) {
        Swal.fire("Error", `Cash (${cashAmt.toFixed(2)}) + Bank/Online (${cashlessAmt.toFixed(2)}) must equal Net Amount (${net.toFixed(2)})`, "error");
        return;
      }

      if (cashlessAmt > 0) {
        const hasAccount = !!form.BankAccountId;
        const hasRef = !!form.PaymentReferenceNo && form.PaymentReferenceNo.trim() !== "";
        
        if (!hasAccount && !hasRef) {
          Swal.fire("Error", "Please select a Bank Account OR enter a Reference No for the Bank/Online amount.", "error");
          return;
        }
        if (hasAccount && hasRef) {
          Swal.fire("Error", "Please provide either a Bank Account OR a Reference No, but not both.", "error");
          return;
        }
      }

      const payDate = form.PaymentDate instanceof Date ? form.PaymentDate : new Date(form.PaymentDate);
      const now = new Date();
      const payDateTime = new Date(payDate.getFullYear(), payDate.getMonth(), payDate.getDate(), now.getHours(), now.getMinutes(), now.getSeconds());

      const payload = {
        PaymentId: form.PaymentId,
        PaymentNo: form.PaymentNo,
        PaymentDate: formatDateForAPI(payDateTime, true),
        BusinessId: form.BusinessId,
        BranchId: form.BranchId || null,
        PayeeType: "sup",
        PayeeId: form.PayeeId,
        Amount: parseFloat(form.Amount) || net,
        TaxAmount: parseFloat(form.TaxAmount) || 0,
        DiscountAmount: parseFloat(form.DiscountAmount) || 0,
        NetAmount: net,
        CashAmount: cashAmt,
        CashlessAmount: cashlessAmt,
        BankAccountId: form.BankAccountId || null,
        PaymentReferenceNo: form.PaymentReferenceNo || null,
        Notes: form.Notes || null,
        CreatedBy: session.userId,
        PaymentDetails: paymentAllocation.map(a => ({
          PurchaseOrderId: a.PurchaseOrderId,
          InvoiceAmount: a.Invoice_Amount,
          PaidAmount: a.PaidAmount,
          TotalAmount: a.TotalAmount,
          SettledAmount: a.SettledAmount,
        })),
        PaymentTaxDetails: [],
      };

      const result = await savePayment(payload);
      if (result.isSuccess) {
        Swal.fire({ icon: "success", title: "Success", text: result.message || "Payment saved successfully" });
        resetForm();
      } else {
        Swal.fire("Error", result.message || "Failed to save payment", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || err.message || "Failed to save payment", "error");
    } finally { setSaveLoading(false); }
  };

  const resetForm = () => {
    setForm({ ...defaultForm, BusinessId: session.companyId || null, BranchId: session.branchId || null, PaymentDate: new Date() });
    setOutstandingInvoices([]);
    setPaymentAllocation([]);
    setTotalAdvance(0);
    setResetCounter(c => c + 1);
    if (form.BusinessId) loadNextPaymentNo();
  };

  const businessOptions = useMemo(() => businesses.map(b => ({ value: b.business_Id, label: b.business_NameEn || b.business_Name })), [businesses]);
  const branchOptions = useMemo(() => !form.BusinessId ? [] : branches.filter(b => b.branch_Buisness_Id === form.BusinessId).map(b => ({ value: b.branch_Id, label: `${b.branch_Code || ""} - ${b.branch_NameEn || ""}`.trim() })), [branches, form.BusinessId]);
  
  const bankOptions = useMemo(() => banks.map(b => ({ value: b.bank_Id || b.bankId, label: b.bank_NameEn || b.bankNameEn || "" })), [banks]);
  
  const bankAccountOptions = useMemo(() => {
    if (!form.BankId) return [];
    return bankAccounts
      .filter(b => b.bankAccount_Bank_Id === form.BankId || b.bank_Id === form.BankId || b.bankId === form.BankId)
      .map(b => ({ value: b.bankAccount_Id || b.bankAccountId, label: `${b.bankAccount_AccountNo || b.accountNumber || ""}`.trim() }));
  }, [bankAccounts, form.BankId]);

  const fmt = (n) => parseFloat(n || 0).toFixed(2);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB") : "-";
  
  const totalOutstanding = useMemo(() => outstandingInvoices.reduce((s, i) => s + (i.outstanding_Amount || 0), 0), [outstandingInvoices]);

  if (!hasPermission) return null;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Add Payment" breadcrumbItem="Add Payment" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  {hasEditOrHigher ? (
                    <>
                      <h5 className="card-title mb-4">Enter Payment</h5>
                      <Form>
                        {/* Row 1: Company / Branch / Supplier / Date */}
                        <Row className="mb-3">
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label>Company <span className="text-danger">*</span></Label>
                              <Select
                                value={businessOptions.find(o => o.value === form.BusinessId) || null}
                                onChange={o => { handleFormChange("BusinessId", o?.value || null); handleFormChange("BranchId", null); }}
                                options={businessOptions}
                                isDisabled={isBusinessReadonly}
                                placeholder="Select Company"
                                styles={selectStyles}
                                isClearable={!isBusinessReadonly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label>Branch</Label>
                              <Select
                                value={branchOptions.find(o => o.value === form.BranchId) || null}
                                onChange={o => handleFormChange("BranchId", o?.value || null)}
                                options={branchOptions}
                                isDisabled={isBranchReadonly || !form.BusinessId}
                                placeholder="Select Branch"
                                styles={selectStyles}
                                isClearable={!isBranchReadonly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label>Payment Date <span className="text-danger">*</span></Label>
                              <DatePicker
                                selected={form.PaymentDate}
                                onChange={d => handleFormChange("PaymentDate", d)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                placeholderText="Select date"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label>Payment No</Label>
                              <Input value={form.PaymentNo} readOnly className="bg-light" />
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Row 2: Supplier search */}
                        <Row className="mb-3">
                          <Col md={6}>
                            <FormGroup className="mb-0">
                              <Label>Supplier <span className="text-danger">*</span></Label>
                              <SupplierSearch
                                key={`supplier-search-${resetCounter}`}
                                businessId={form.BusinessId}
                                onSelect={sup => handleFormChange("PayeeId", sup ? sup.supplier_Id : null)}
                                isDisabled={!form.BusinessId || isViewOnly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label>Amount <span className="text-danger">*</span></Label>
                              <Input
                                type="number"
                                value={form.Amount}
                                onChange={e => handleAmountChange(e.target.value)}
                                placeholder="0.00"
                                readOnly={isViewOnly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label>Net Amount</Label>
                              <Input value={fmt(form.NetAmount)} readOnly className="bg-light fw-bold" />
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Row 3: Cash / Cashless / Bank / Ref */}
                        <Row className="mb-3">
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label>Cash Amount</Label>
                              <Input
                                type="number"
                                value={form.CashAmount}
                                onChange={e => handleCashAmountChange(e.target.value)}
                                placeholder="0.00"
                                readOnly={isViewOnly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label>Bank/Online</Label>
                              <Input value={fmt(form.CashlessAmount)} readOnly className="bg-light" />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label>Bank</Label>
                              <Select
                                value={bankOptions.find(o => o.value === form.BankId) || null}
                                onChange={o => { handleFormChange("BankId", o?.value || null); handleFormChange("BankAccountId", null); }}
                                options={bankOptions}
                                isClearable
                                placeholder="Select bank"
                                styles={selectStyles}
                                isDisabled={isViewOnly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label>Bank Account {(parseFloat(form.CashlessAmount) > 0 && !form.PaymentReferenceNo) && <span className="text-danger">*</span>}</Label>
                              <Select
                                value={bankAccountOptions.find(o => o.value === form.BankAccountId) || null}
                                onChange={o => handleFormChange("BankAccountId", o?.value || null)}
                                options={bankAccountOptions}
                                isClearable
                                placeholder="Select account"
                                styles={selectStyles}
                                isDisabled={isViewOnly || !form.BankId}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label>Reference No {(parseFloat(form.CashlessAmount) > 0 && !form.BankAccountId) && <span className="text-danger">*</span>}</Label>
                              <Input
                                value={form.PaymentReferenceNo}
                                onChange={e => handleFormChange("PaymentReferenceNo", e.target.value)}
                                placeholder="e.g. TXN123"
                                readOnly={isViewOnly}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Row 4: Notes */}
                        <Row className="mb-4">
                          <Col md={12}>
                            <FormGroup className="mb-0">
                              <Label>Notes</Label>
                              <Input
                                type="textarea"
                                rows={2}
                                value={form.Notes}
                                onChange={e => handleFormChange("Notes", e.target.value)}
                                placeholder="Optional notes..."
                                readOnly={isViewOnly}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Action buttons */}
                        {!isViewOnly && (
                          <Row className="mb-4">
                            <Col>
                              <Button color="primary" onClick={handleSave} disabled={saveLoading} className="me-2">
                                {saveLoading ? <ClipLoader size={16} color="#fff" /> : "Save Payment"}
                              </Button>
                              <Button color="secondary" onClick={resetForm} outline>Reset</Button>
                            </Col>
                          </Row>
                        )}
                      </Form>
                    </>
                  ) : (
                    <div className="text-muted text-center py-4">You have view-only access to this page.</div>
                  )}

                  {/* Outstanding Invoices Allocation Table */}
                  {form.PayeeId && (
                    <div className="mt-2">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">
                          Outstanding Purchase Invoices
                          {loading && <ClipLoader size={14} color="#556ee6" className="ms-2" />}
                        </h6>
                        <div className="text-end">
                          <span className="me-3">Total Outstanding: <strong className="text-danger">{fmt(totalOutstanding)}</strong></span>
                          {/* <span>Unallocated Advance: <strong className="text-success">{fmt(totalAdvance)}</strong></span> */}
                        </div>
                      </div>
                      
                      {outstandingInvoices.length === 0 && !loading ? (
                        <div className="alert alert-info">
                          No outstanding invoices found for this supplier. 
                          {/* {totalAdvance > 0 && <span> This supplier has a total unallocated advance credit of <strong>{fmt(totalAdvance)}</strong>.</span>} */}
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <Table bordered size="sm">
                            <thead className="table-light">
                              <tr>
                                <th>Invoice No</th>
                                <th>Date</th>
                                <th className="text-end">Invoice Amount</th>
                                <th className="text-end">Already Paid</th>
                                <th className="text-end">Outstanding</th>
                                <th className="text-end text-primary">Paying Now</th>
                                <th className="text-end">Remaining After</th>
                              </tr>
                            </thead>
                            <tbody>
                              {outstandingInvoices.map(inv => {
                                const alloc = paymentAllocation.find(a => a.PurchaseOrderId === inv.purchaseOrder_Id);
                                return (
                                  <tr key={inv.purchaseOrder_Id}>
                                    <td>{inv.purchaseOrder_InvoiceNo || "-"}</td>
                                    <td>{fmtDate(inv.purchaseOrder_Date)}</td>
                                    <td className="text-end">{fmt(inv.invoice_Amount)}</td>
                                    <td className="text-end">{fmt(inv.paid_Amount)}</td>
                                    <td className="text-end text-danger">{fmt(inv.outstanding_Amount)}</td>
                                    <td className="text-end text-primary fw-bold">{alloc ? fmt(alloc.PaidAmount) : "0.00"}</td>
                                    <td className="text-end">{alloc ? fmt(alloc.OutstandingAfter) : fmt(inv.outstanding_Amount)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            {paymentAllocation.length > 0 && (
                              <tfoot className="table-light">
                                <tr>
                                  <td colSpan={5} className="text-end fw-bold">Total Allocated:</td>
                                  <td className="text-end fw-bold text-primary">
                                    {fmt(paymentAllocation.reduce((s, a) => s + a.PaidAmount, 0))}
                                  </td>
                                  <td></td>
                                </tr>
                              </tfoot>
                            )}
                          </Table>
                        </div>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

// =============================================
// Inline supplier search component
// =============================================
function SupplierSearch({ businessId, onSelect, isDisabled }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [timer, setTimer] = useState(null);

  const handleInputChange = (val) => {
    clearTimeout(timer);
    if (!val || val.length < 2) { setOptions([]); return; }
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await searchSuppliers(val, 1, 20);
        const items = res?.items || res || [];
        setOptions(items.map(s => ({ value: s.supplier_Id, label: `${s.supplier_NameEn || s.supplier_NameAr || ""} (${s.supplier_Code || ""})`, data: s })));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }, 400);
    setTimer(t);
  };

  const handleChange = (opt) => {
    setSelected(opt);
    onSelect(opt ? opt.data : null);
  };

  return (
    <Select
      value={selected}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={options}
      isLoading={loading}
      isClearable
      isDisabled={isDisabled || !businessId}
      placeholder="Search supplier..."
      noOptionsMessage={({ inputValue }) => inputValue && inputValue.length >= 2 ? "No suppliers found" : "Type to search..."}
      styles={{
        control: (p, s) => ({ ...p, minHeight: "38px", borderColor: s.isFocused ? "#556ee6" : "#ced4da", boxShadow: s.isFocused ? "0 0 0 0.2rem rgba(85,110,230,.25)" : "none" }),
        menu: (p) => ({ ...p, zIndex: 9999 }),
      }}
    />
  );
}
